import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import VisibilityIcon from '@mui/icons-material/Visibility'
import API from '../../../../services/API'
import BigModal from '../../../../components/Modals/BigModal'
import QuoteBuilder from '../../planning/Quotes/QuoteBuilder/QuoteBuilder'
import InvoiceGenerator from './InvoiceGenerator'
import { EventMetaContext, ShiftContext } from '../SingleEvent'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import { getTravelPay } from '../../../../helpers/shiftTravel'
import { formatMoney } from '../../../../helpers/currency'

const QUOTE_STATUS_COLOUR = {
  Draft: 'default',
  Sent: 'info',
  Accepted: 'success',
  Declined: 'error',
  Expired: 'warning',
}

const INVOICE_STATUS_COLOUR = {
  Draft: 'default',
  Approved: 'info',
  Sent: 'info',
  'Partially Paid': 'warning',
  Paid: 'success',
  Overdue: 'error',
}

// Payment state wins over the stored status — an invoice recorded as Sent
// but settled in full reads as Paid, and a Sent one past its due date reads
// as Overdue. Drafts stay Drafts whatever their date.
export const deriveInvoiceStatus = (invoice) => {
  if (invoice?.isFullyPaid) return 'Paid'
  if ((invoice?.totalPaid ?? 0) > 0) return 'Partially Paid'
  if (invoice?.status === 'Draft' || invoice?.status === 'Approved') {
    return invoice.status
  }
  if (invoice?.dueDate && moment(invoice.dueDate).isBefore(moment(), 'day')) {
    return 'Overdue'
  }
  return invoice?.status ?? 'Sent'
}

const authorName = (author) =>
  `${author?.firstName ?? ''} ${author?.lastName ?? ''}`.trim()

const StatusChip = ({ label, colour }) => (
  <Chip
    size="small"
    label={label}
    color={colour ?? 'default'}
    variant={colour && colour !== 'default' ? 'filled' : 'outlined'}
    sx={{ borderRadius: 1, height: 20, fontSize: '0.68rem', fontWeight: 700 }}
  />
)

const SummaryTile = ({ label, value, tone }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      p: 1.25,
      borderRadius: 2,
      bgcolor: '#f8fafc',
      border: '1px solid rgba(15, 23, 42, 0.06)',
    }}
  >
    <Typography
      component="div"
      variant="caption"
      noWrap
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.62rem',
      }}
    >
      {label}
    </Typography>
    <Typography
      noWrap
      title={value}
      sx={{ fontWeight: 800, fontSize: '0.9rem', letterSpacing: '-0.01em' }}
      color={tone ? `${tone}.main` : 'text.primary'}
    >
      {value}
    </Typography>
  </Box>
)

const GroupLabel = ({ children, count }) => (
  <Stack direction="row" alignItems="center" gap={0.75}>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.66rem',
      }}
    >
      {children}
    </Typography>
    {count > 0 && (
      <Typography variant="caption" color="text.disabled" fontWeight={700}>
        {count}
      </Typography>
    )}
  </Stack>
)

const PanelRow = ({ icon, iconTone, primary, secondary, right, onClick }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1.25}
    onClick={onClick}
    sx={{
      px: 1,
      py: 0.9,
      borderRadius: 2,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'background-color 0.15s ease',
      '&:hover': onClick ? { bgcolor: 'rgba(15, 23, 42, 0.04)' } : undefined,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.5,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        bgcolor: `${iconTone}.main`,
        color: `${iconTone}.contrastText`,
        opacity: 0.9,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0} flex={1}>
      <Typography variant="body2" fontWeight={700} noWrap>
        {primary}
      </Typography>
      <Typography
        component="div"
        variant="caption"
        color="text.secondary"
        noWrap
      >
        {secondary}
      </Typography>
    </Box>
    {right && (
      <Stack alignItems="flex-end" gap={0.25} flexShrink={0}>
        {right}
      </Stack>
    )}
  </Stack>
)

const InvoicePanel = () => {
  const { eventMeta, loadEvent } = useContext(EventMetaContext)
  const { shifts } = useContext(ShiftContext)
  const { orgConfig } = useContext(TenantSettingsContext)
  const { setToast } = useContext(GlobalContext)
  // Symbol or ISO code depending on tenant setup; formatMoney resolves both
  const currency = orgConfig?.orgSettings?.currency

  const [invoices, setInvoices] = useState([])
  const [loadingInvoices, setLoadingInvoices] = useState(true)
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null)
  const [openQuoteBuilder, setOpenQuoteBuilder] = useState(false)
  const [existingQuote, setExistingQuote] = useState(null)
  const [viewQuoteOnly, setViewQuoteOnly] = useState(false)
  const [openCreateInvoice, setOpenCreateInvoice] = useState(false)
  const [createInvoiceFromQuote, setCreateInvoiceFromQuote] = useState(null)
  const [selectedInvoice, setSelectedInvoice] = useState(false)

  const loadInvoices = () => {
    setLoadingInvoices(true)
    API.get('/event-invoices?event=' + eventMeta._id)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setInvoices(res.data)
        }
      })
      .finally(() => setLoadingInvoices(false))
  }

  useEffect(() => {
    loadInvoices()
  }, [eventMeta._id])

  const quotes = useMemo(
    () =>
      [...(eventMeta?.quotes ?? [])].sort(
        (a, b) => moment(b.createdAt) - moment(a.createdAt),
      ),
    [eventMeta?.quotes],
  )

  const sortedInvoices = useMemo(
    () =>
      [...invoices].sort(
        (a, b) =>
          moment(b.invoiceDate ?? b.createdAt) -
          moment(a.invoiceDate ?? a.createdAt),
      ),
    [invoices],
  )

  const latestQuote = quotes[0]
  // Invoices are only raised from the quote the customer actually agreed to -
  // "latest" used to pick up Draft and even Declined quotes
  const acceptedQuote = quotes.find((quote) => quote.status === 'Accepted')

  // The legacy "Total Wages" figure, kept here as a margin sanity check
  // beside the quote. Fractional hours are costed exactly.
  const estimatedWages = useMemo(
    () =>
      (shifts ?? []).reduce((total, shift) => {
        const bonusTotal = (shift?.bonuses ?? []).reduce(
          (sum, bonus) => sum + (Number(bonus?.amount) || 0),
          0,
        )
        const travelTotal = getTravelPay(shift?.travel) || 0
        const payRate = Number(shift?.payRate) || 0
        const base = shift?.hourlyRate
          ? payRate * moment(shift.end).diff(shift.start, 'hours', true)
          : payRate
        return total + (Number.isFinite(base) ? base : 0) + bonusTotal + travelTotal
      }, 0),
    [shifts],
  )

  const invoicedTotal = sortedInvoices.reduce(
    (sum, invoice) => sum + (Number(invoice.netAmount ?? invoice.totalAmount) || 0),
    0,
  )
  const outstandingTotal = sortedInvoices.reduce(
    (sum, invoice) => sum + Math.max(Number(invoice.remainingBalance) || 0, 0),
    0,
  )

  const closeAddMenu = () => setAddMenuAnchorEl(null)

  const openNewQuote = () => {
    closeAddMenu()
    setExistingQuote(null)
    setViewQuoteOnly(false)
    setOpenQuoteBuilder(true)
  }

  const openQuote = (quoteID, viewOnly) => {
    setExistingQuote(quoteID)
    setViewQuoteOnly(viewOnly)
    setOpenQuoteBuilder(true)
  }

  const openNewInvoice = () => {
    closeAddMenu()
    setCreateInvoiceFromQuote(null)
    setOpenCreateInvoice(true)
  }

  const deleteDraftQuote = async (quoteID) => {
    if (!window.confirm('Delete this draft quote? This cannot be undone.')) return
    try {
      const res = await API.delete(`/clients/quote/${quoteID}`)
      if (res.status === 200) {
        setToast?.({ message: 'Draft quote deleted' })
        loadEvent()
      } else {
        setToast?.({
          message: res?.data?.error || 'Unable to delete this quote.',
          severity: 'error',
        })
      }
    } catch {
      setToast?.({
        message: 'Unable to delete this quote. Please try again later.',
        severity: 'error',
      })
    }
  }

  const openInvoiceFromAcceptedQuote = () => {
    closeAddMenu()
    if (!acceptedQuote) return
    setCreateInvoiceFromQuote(acceptedQuote)
    setOpenCreateInvoice(true)
  }

  const isEmpty = quotes.length === 0 && sortedInvoices.length === 0

  return (
    <Box
      component={Paper}
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
        bgcolor: 'background.paper',
      }}
    >
      <Stack gap={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Stack gap={0.25}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Invoicing
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Quotes, invoices and payments for this event
            </Typography>
          </Stack>
          <Tooltip title="Create a quote or invoice">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => setAddMenuAnchorEl(e.currentTarget)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            open={!!addMenuAnchorEl}
            onClose={closeAddMenu}
            anchorEl={addMenuAnchorEl}
          >
            <MenuItem onClick={openNewQuote}>
              <ListItemIcon>
                <RequestQuoteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Create quote</ListItemText>
            </MenuItem>
            <MenuItem onClick={openNewInvoice}>
              <ListItemIcon>
                <ReceiptLongIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Create invoice</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={openInvoiceFromAcceptedQuote}
              disabled={!acceptedQuote}
            >
              <ListItemIcon>
                <ReceiptLongIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Invoice from accepted quote"
                secondary={
                  acceptedQuote
                    ? `${acceptedQuote.ref ? `${acceptedQuote.ref} — ` : ''}${formatMoney(acceptedQuote.totalInvoice, currency)}`
                    : 'No accepted quote yet'
                }
              />
            </MenuItem>
          </Menu>
        </Stack>

        <Stack direction="row" gap={1}>
          <SummaryTile
            label="Quoted"
            value={
              latestQuote ? formatMoney(latestQuote.totalInvoice, currency) : '—'
            }
          />
          <SummaryTile
            label="Invoiced"
            value={
              sortedInvoices.length ? formatMoney(invoicedTotal, currency) : '—'
            }
          />
          <SummaryTile
            label="Outstanding"
            value={
              sortedInvoices.length
                ? formatMoney(outstandingTotal, currency)
                : '—'
            }
            tone={
              sortedInvoices.length
                ? outstandingTotal > 0
                  ? 'error'
                  : 'success'
                : undefined
            }
          />
        </Stack>

        {loadingInvoices && <LinearProgress />}

        {!loadingInvoices && isEmpty && (
          <Stack
            alignItems="center"
            gap={1}
            sx={{
              py: 2.5,
              px: 2,
              borderRadius: 2,
              border: '1px dashed rgba(15, 23, 42, 0.18)',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              Nothing quoted or invoiced yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Build a quote from the shift plan, then turn it into an invoice
              once the client accepts.
            </Typography>
            <Stack direction="row" gap={1} pt={0.5}>
              <Button size="small" variant="contained" onClick={openNewQuote}>
                Create quote
              </Button>
              <Button size="small" variant="outlined" onClick={openNewInvoice}>
                Create invoice
              </Button>
            </Stack>
          </Stack>
        )}

        {quotes.length > 0 && (
          <Stack gap={0.5}>
            <GroupLabel count={quotes.length}>Quotes</GroupLabel>
            <Stack>
              {quotes.map((quote) => (
                <PanelRow
                  key={quote._id}
                  icon={<RequestQuoteIcon fontSize="small" />}
                  iconTone="anPurple"
                  primary={formatMoney(quote.totalInvoice, currency)}
                  secondary={[
                    moment(quote.createdAt).format('Do MMM YYYY'),
                    authorName(quote.author),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  onClick={() => openQuote(quote._id, true)}
                  right={
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <StatusChip
                        label={quote.status ?? 'Draft'}
                        colour={QUOTE_STATUS_COLOUR[quote.status ?? 'Draft']}
                      />
                      <Tooltip title="View quote">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            openQuote(quote._id, true)
                          }}
                        >
                          <VisibilityIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          (quote.status ?? 'Draft') === 'Draft'
                            ? 'Edit quote'
                            : 'Create new version'
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            openQuote(quote._id, false)
                          }}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      {(quote.status ?? 'Draft') === 'Draft' && (
                        <Tooltip title="Delete draft quote">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              deleteDraftQuote(quote._id)
                            }}
                          >
                            <DeleteOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  }
                />
              ))}
            </Stack>
          </Stack>
        )}

        {quotes.length > 0 && sortedInvoices.length > 0 && (
          <Divider sx={{ opacity: 0.6 }} />
        )}

        {sortedInvoices.length > 0 && (
          <Stack gap={0.5}>
            <GroupLabel count={sortedInvoices.length}>Invoices</GroupLabel>
            <Stack>
              {sortedInvoices.map((invoice) => {
                const status = deriveInvoiceStatus(invoice)
                const balance = Number(invoice.remainingBalance) || 0
                return (
                  <PanelRow
                    key={invoice._id}
                    icon={<ReceiptLongIcon fontSize="small" />}
                    iconTone="anMainBlue"
                    primary={invoice.reference}
                    secondary={`Due ${moment(invoice.dueDate).format(
                      'Do MMM YYYY',
                    )} · ${formatMoney(
                      invoice.netAmount ?? invoice.totalAmount,
                      currency,
                    )}`}
                    onClick={() => setSelectedInvoice(invoice)}
                    right={
                      <>
                        <StatusChip
                          label={status}
                          colour={INVOICE_STATUS_COLOUR[status]}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color={balance > 0 ? 'error.main' : 'success.main'}
                        >
                          {balance > 0
                            ? `${formatMoney(balance, currency)} due`
                            : 'Settled'}
                        </Typography>
                      </>
                    }
                  />
                )
              })}
            </Stack>
          </Stack>
        )}

        <Divider sx={{ opacity: 0.6 }} />
        <Stack direction="row" justifyContent="space-between" gap={1}>
          <Typography variant="caption" color="text.secondary">
            Estimated staff wages from shifts
          </Typography>
          <Typography variant="caption" fontWeight={700}>
            {formatMoney(estimatedWages, currency)}
          </Typography>
        </Stack>
      </Stack>

      <InvoiceGenerator
        open={openCreateInvoice || !!selectedInvoice}
        existingInvoice={selectedInvoice}
        createInvoiceFromQuote={createInvoiceFromQuote}
        onClose={() => {
          setOpenCreateInvoice(false)
          setCreateInvoiceFromQuote(null)
          setSelectedInvoice(false)
        }}
        loadInvoices={loadInvoices}
      />
      <BigModal
        open={openQuoteBuilder}
        onClose={() => {
          setOpenQuoteBuilder(false)
          setExistingQuote(null)
          // A send/accept/decline inside the builder changes the quote's
          // status - refresh so the panel's chips don't go stale
          loadEvent()
        }}
        noPaper
      >
        <QuoteBuilder
          existingEvent={eventMeta}
          hasExistingQuote={existingQuote}
          viewOnly={viewQuoteOnly}
          onClose={() => {
            setOpenQuoteBuilder(false)
            setExistingQuote(null)
            loadEvent()
          }}
        />
      </BigModal>
    </Box>
  )
}

export default React.memo(InvoicePanel)
